import { useEffect, useState } from 'react'

// Lightweight global toast. Fire from anywhere with `showToast(...)`.
// Auto-dismisses after 3.5s. Stacks bottom-up if multiple fire.
//
// Why a custom one rather than a library: a ~50-line file vs adding
// react-toastify (40KB) — and the styling matches the rest of the app.

export type ToastKind = 'success' | 'info' | 'warn' | 'error'
export interface ToastInput { kind?: ToastKind; icon?: string; message: string; ms?: number }

interface ActiveToast extends ToastInput { id: number; createdAt: number }

let nextId = 1
const listeners = new Set<(list: ActiveToast[]) => void>()
let stack: ActiveToast[] = []

function emit() { listeners.forEach(fn => fn(stack)) }

export function showToast(input: ToastInput) {
  const t: ActiveToast = {
    id: nextId++,
    kind: input.kind || 'success',
    icon: input.icon,
    message: input.message,
    ms: input.ms ?? 3500,
    createdAt: Date.now(),
  }
  stack = [...stack, t]
  emit()
  setTimeout(() => { stack = stack.filter(x => x.id !== t.id); emit() }, t.ms)
}

export default function ToastHost() {
  const [items, setItems] = useState<ActiveToast[]>([])
  useEffect(() => { listeners.add(setItems); setItems(stack); return () => { listeners.delete(setItems) } }, [])
  if (items.length === 0) return null
  return (
    <div aria-live="polite" style={{
      position: 'fixed', left: '50%', transform: 'translateX(-50%)',
      top: 'calc(env(safe-area-inset-top, 0px) + 64px)',
      zIndex: 900, display: 'flex', flexDirection: 'column', gap: 8,
      maxWidth: 'calc(100vw - 24px)', pointerEvents: 'none',
    }}>
      {items.map(t => (
        <div key={t.id} role="status" style={{
          padding: '10px 16px', borderRadius: 999,
          background: t.kind === 'error' ? '#7C2D12'
            : t.kind === 'warn' ? '#92400E'
            : t.kind === 'info' ? '#1E3A2E'
            : '#155534',
          color: '#fff', fontSize: 13, fontWeight: 600,
          boxShadow: '0 8px 20px -8px rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', gap: 8,
          pointerEvents: 'auto',
        }}>
          {t.icon && <span>{t.icon}</span>}
          <span>{t.message}</span>
        </div>
      ))}
    </div>
  )
}
